import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import api from '../utils/api';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categoriaActiva, setCategoriaActiva] = useState('all');
  const [configuracion, setConfiguracion] = useState(null);
  const [loading, setLoading] = useState(true);

  const categorias = [
    { 
      id: 'Prevención', 
      nombre: 'Prevención',
      color: 'bg-green-500',
      descripcion: 'Tips para prevenir enfermedades',
      imagen: 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=800'
    },
    { 
      id: 'Educación', 
      nombre: 'Educación',
      color: 'bg-blue-500',
      descripcion: 'Información educativa veterinaria',
      imagen: 'https://images.unsplash.com/photo-1574158622682-e40e69881006?w=800'
    },
    { 
      id: 'Consejos', 
      nombre: 'Consejos',
      color: 'bg-purple-500',
      descripcion: 'Consejos prácticos para dueños',
      imagen: 'https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?w=800'
    }
  ];

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [postsResponse, configResponse] = await Promise.all([
        api.get('/blog/posts/published'),
        api.get('/configuracion/seccion/blog').catch(() => ({ data: {} }))
      ]);
      setPosts(postsResponse.data);
      setConfiguracion(configResponse.data);
    } catch (error) {
      console.error('Error loading blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtrarPorCategoria = async (categoria) => {
    setCategoriaActiva(categoria);
    try {
      if (categoria === 'all') {
        const response = await api.get('/blog/posts/published');
        setPosts(response.data);
      } else {
        const response = await api.get(`/blog/posts/categoria/${categoria}`);
        setPosts(response.data);
      }
    } catch (error) {
      console.error('Error filtering:', error);
    }
  };

  const getCategoriaColor = (categoria) => {
    const cat = categorias.find(c => c.id === categoria);
    return cat ? cat.color : 'bg-gray-500';
  };

  const textos = configuracion?.textos || {};
  const imagenes = configuracion?.imagenes || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {textos.titulo_principal || 'Interactúa con el blog de MoraBioVet'}
          </h1>
          <p className="text-xl text-blue-100">
            Consejos, educación y prevención para el cuidado de tus mascotas
          </p>
        </div>
      </section>

      {/* Categorías Destacadas */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {categorias.map((categoria) => (
              <Card 
                key={categoria.id}
                className="overflow-hidden cursor-pointer hover:shadow-xl transition-shadow border-2 hover:border-[#1e40af]"
                onClick={() => filtrarPorCategoria(categoria.id)}
              >
                <div className="relative h-48">
                  <img
                    src={imagenes[categoria.id.toLowerCase()] || categoria.imagen}
                    alt={categoria.nombre}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <span className={`${categoria.color} text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg`}>
                      {textos[`categoria_${categoria.id.toLowerCase()}`] || categoria.nombre}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-600">{categoria.descripcion}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button
              onClick={() => filtrarPorCategoria('all')}
              className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
            >
              <BookOpen className="mr-2 h-4 w-4" />
              {textos.boton_ver_todos || 'Ver todos los artículos'}
            </Button>
          </div>
        </div>
      </section>

      {/* Lista de Posts */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* Filtros */}
          {categoriaActiva !== 'all' && (
            <div className="mb-6 flex items-center gap-3">
              <span className="text-gray-600">Mostrando:</span>
              <span className={`${getCategoriaColor(categoriaActiva)} text-white px-4 py-2 rounded-full text-sm font-semibold`}>
                {categoriaActiva}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => filtrarPorCategoria('all')}
              >
                Ver todos
              </Button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e40af] mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-2xl font-bold text-gray-700 mb-2">
                No hay posts publicados aún
              </h3>
              <p className="text-gray-500">
                {categoriaActiva !== 'all' 
                  ? `No hay posts en la categoría "${categoriaActiva}"`
                  : 'Pronto habrá contenido nuevo'
                }
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-shadow border-2">
                  {post.imagen && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={post.imagen}
                        alt={post.titulo}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    {post.categoria && (
                      <span className={`${getCategoriaColor(post.categoria)} text-white px-3 py-1 rounded-full text-xs font-semibold mb-3 inline-block`}>
                        {post.categoria}
                      </span>
                    )}
                    <h3 className="text-xl font-bold text-[#1e3a8a] mb-3 line-clamp-2">
                      {post.titulo}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {post.resumen || post.contenido?.substring(0, 150) + '...'}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{post.autor_nombre}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {new Date(post.fecha_publicacion || post.created_at).toLocaleDateString('es-MX')}
                        </span>
                      </div>
                    </div>
                    <Button
                      asChild
                      className="w-full bg-[#84cc16] hover:bg-[#65a30d] text-white"
                    >
                      <Link to={`/blog/${post.slug || post.id}`}>
                        Leer más
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
